// Now (`/hub/now`) data layer.
//
// Modelled on the canonical `conversationRpc` wrapper (src/api/conversation.js):
// throw-on-error, unwrap the `{error:true, message}` RPC-gate envelope, label errors
// by RPC name, one jittered retry on a transport (not database) failure. Imports the
// singleton Supabase client from `../lib/supabase`.
//
// The RPCs below are DEPLOYED (verified live 2026-09-07): fn_now, fn_now_takeaway,
// fn_now_dismiss, v_now_by_person, and the five exit actions. The live supabase.rpc(...)
// path is the default. HUB_STUB is a local-dev-only fixtures override: set
// REACT_APP_HUB_STUB=1 to render against the __fixtures__ payloads without a backend.
//
// id types: fn_now_dismiss takes p_id as TEXT — it absorbs any signal's id shape (one
// detector returns a source key, not a row id) — while the five exit actions each write
// to exactly one table and take a real uuid. PostgREST casts from the function signature,
// so the FE passes item.id through as-is in both cases.

import { supabase } from "../../lib/supabase";
import { dedupe, rpcKey } from "../rpcCache";
import fnNowFull from "../../components/hub/now/__fixtures__/fn_now.full.json";
import fnNowTakeaway from "../../components/hub/now/__fixtures__/fn_now.takeaway.json";
import vNowByPerson from "../../components/hub/now/__fixtures__/v_now_by_person.json";
import fnPromiseDetail from "../../components/hub/now/__fixtures__/fn_promise_detail.json";

const HUB_STUB =
  typeof process !== "undefined" &&
  !!process.env &&
  process.env.REACT_APP_HUB_STUB === "1";

// A refused HTTP/2 stream is a transport failure, not a database one — retry once.
// A Postgres error / RLS refusal / bad parameter carries a `code` and fails on the
// first attempt, where it belongs.
const TRANSPORT_FAILURE =
  /failed to fetch|networkerror|load failed|err_http2|connection closed/i;
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const isTransportFailure = (err) =>
  !err?.code && TRANSPORT_FAILURE.test(err?.message || "");

async function nowRpc(name, params, label) {
  const attempt = async () => {
    try {
      return await supabase.rpc(name, params);
    } catch (e) {
      return { data: null, error: e };
    }
  };
  let result = await attempt();
  if (isTransportFailure(result.error)) {
    await sleep(200 + Math.floor(Math.random() * 250));
    result = await attempt();
  }
  const { data, error } = result;
  if (error) {
    const detail = error?.message || "Supabase RPC failed";
    throw new Error(label ? `${label}: ${detail}` : detail);
  }
  // The gate contract: an RPC gates itself and returns {error:true, message} rather
  // than throwing. A gate/refusal message is user-facing business text (e.g. a
  // needs_assistance refusal, "move the work with fn_work_move") — surface it VERBATIM,
  // without the dev-facing RPC-name label, so the page can show exactly what to do.
  if (data && typeof data === "object" && data.error === true) {
    throw new Error(data.message || `${name} returned an error`);
  }
  return data;
}

/**
 * The ranked feed. Returns `{ for, total, by_kind, items[] }`, or — for a caller
 * without ops access — `null` / a `permitted:false` payload the page reads as gated.
 *
 * "Mine" (the default) passes NO p_owner_id — the RPC defaults to the JWT caller.
 *
 * Identical concurrent reads share one request. A caller that stamps its own start time on
 * the result (the page's poll, which uses it to tell what the reader has since cleared) must
 * pass `dedupe: false`: joining a read that began earlier would pair that older snapshot with
 * a later stamp.
 * @param {{everyone?: boolean, clientId?: string, limit?: number, offset?: number, dedupe?: boolean}} [opts]
 */
export async function getNow({ everyone = false, clientId, limit = 50, offset = 0, dedupe: share = true } = {}) {
  if (HUB_STUB) return fnNowFull;
  // p_offset (default 0) was added backend-side 2026-09-07 so the feed can page past
  // the top p_limit — total (e.g. 542) and the pageable rows are now consistent.
  const params = { p_everyone: everyone, p_limit: limit, p_offset: offset };
  if (clientId) params.p_client_id = clientId;
  const read = () => nowRpc("fn_now", params, "now feed");
  if (!share) return read();
  // Dedupe in-flight: StrictMode's double-invoke and any concurrent mount that
  // asks for the exact same scope+window produce ONE fn_now call (perf memo §2).
  // The other-scope total probe (different p_everyone) and another page (different
  // p_offset) keep their own keys, so they are never collapsed.
  return dedupe(rpcKey("fn_now", params), read);
}

/**
 * The takeaway sentence — the ONLY payload that carries an explicit tint.
 *
 * @deprecated as of the perf-memo §4.1 change: `fn_now` now returns the
 *   `takeaway` key inline — the SAME `{tint, says}` object this function
 *   returned (verified identical on live data). `fn_now_takeaway` worked by
 *   calling `fn_now` again, so every Now page load ran all eleven detectors
 *   twice. Read `takeaway` off the `getNow` response and stop calling this —
 *   one fewer round trip (~520 ms warm, ~1.6 s cold). Both paths agree until
 *   the call site switches, so there is no coordinated deploy. Kept exported
 *   only so existing imports/tests don't break; do not add new call sites.
 * @param {{everyone?: boolean}} [opts]
 * @returns {Promise<{says: string, tint: string}>}
 */
export async function getNowTakeaway({ everyone = false } = {}) {
  if (HUB_STUB) return fnNowTakeaway;
  return nowRpc("fn_now_takeaway", { p_everyone: everyone }, "now takeaway");
}

/**
 * Silence a signal. A real, reversible exit — never a hide flag.
 * @param {{kind: string, id: string, note?: string|null}} arg
 */
export async function dismissNow({ kind, id, note = null }) {
  if (HUB_STUB) return { ok: true };
  return nowRpc(
    "fn_now_dismiss",
    { p_kind: kind, p_id: id, p_note: note },
    "now dismiss",
  );
}

// A person row counts as active if any kind counter is nonzero — v_now_by_person is
// NOT a leaderboard, so idle people are simply omitted, never ranked or reddened.
const PERSON_META_KEYS = new Set(["person_id", "person_name"]);
const hasAnyCount = (row) =>
  Object.entries(row).some(
    ([k, v]) => !PERSON_META_KEYS.has(k) && typeof v === "number" && v > 0,
  );

/**
 * Everyone-scope per-person breakdown for managers. A plain `select *` per the spec;
 * filtered client-side to rows with any nonzero counter.
 *
 * STAGED: not yet wired into NowPage (the Everyone switch currently re-fetches the flat
 * feed via getNow({everyone:true})). Kept as the data dependency for the per-person
 * breakdown view; wire it in the PR that builds that surface.
 */
export async function getNowByPerson() {
  if (HUB_STUB) return vNowByPerson.filter(hasAnyCount);
  const { data, error } = await supabase.from("v_now_by_person").select("*");
  if (error) throw new Error(`now by person: ${error.message}`);
  return (data || []).filter(hasAnyCount);
}

// ─── Per-kind EXIT actions ──────────────────────────────────────────────────
// RPC names/signatures verified live 2026-09-07. Each writes to exactly one table,
// keyed by the row's subject_type: commitment → fn_commitment_met, task →
// fn_request_open / fn_chase_client, message → fn_agent_release / fn_agent_edit.
// markPromiseMet hits the commitments RPC DIRECTLY; it must NEVER be routed
// through fn_now_dismiss (that would make one function mean two opposite things).
//
// `fn_work_move` (task → move work to a chosen status) is NOT wrapped here: it
// lives in src/api/work.js (the one canonical wrapper) and is called by the
// <MoveWorkMenu> component (D4 / §A-1). needs_assistance rows offer that menu,
// not dismiss — fn_now_dismiss refuses them with "move the work with fn_work_move".

export async function markPromiseMet(item) {
  if (HUB_STUB) return { ok: true };
  return nowRpc("fn_commitment_met", { p_id: item.id }, "promise met");
}

/**
 * One promise's detail for the Now panel: the promise, its source email, its conversation
 * and task, and the three action flags (`can_mark_done`, `can_withdraw`, `can_make_task`).
 * `null` is the RPC's answer to a caller it will not serve.
 * @param {string} id  The commitment's id (the `fn_now` item id).
 */
export async function getPromiseDetail(id) {
  if (HUB_STUB) return fnPromiseDetail;
  return nowRpc("fn_promise_detail", { p_id: id }, "promise detail");
}

/**
 * Tie a promise to the task made from it. The promise then closes itself when that task
 * is finished.
 * @param {{id: string, taskId: string}} arg
 */
export async function linkPromiseTask({ id, taskId }) {
  if (HUB_STUB) return { ok: true };
  return nowRpc(
    "fn_promise_link_task",
    { p_id: id, p_task_id: taskId },
    "promise link task",
  );
}

export async function chaseClient(item) {
  if (HUB_STUB) return { ok: true };
  // fn_chase_client takes p_note (confirmed 2026-09-07). "Chase" is one-click with no
  // note field, so pass null — the RPC records the chase without a note.
  return nowRpc("fn_chase_client", { p_id: item.id, p_note: null }, "chase client");
}

// fn_work_move(id, status) takes a target status — moving work is a deliberate choice,
// not a one-click. The <MoveWorkMenu> component (D4 / §A-1) reads the status list
// from ops_work_types.statuses and calls the canonical moveWork in src/api/work.js.
// No wrapper here — see the exit-actions note above.
export async function releaseAgent(item) {
  if (HUB_STUB) return { ok: true };
  return nowRpc("fn_agent_release", { p_id: item.id }, "release agent");
}

// Edit returns the agent's work to draft — the OPPOSITE of release. Kept a distinct
// RPC so the Edit affordance never performs a release (spec §4).
export async function editAgent(item) {
  if (HUB_STUB) return { ok: true };
  // fn_agent_edit takes p_note (confirmed 2026-09-07). "Back to draft" is one-click, no
  // note field — pass null.
  return nowRpc("fn_agent_edit", { p_id: item.id, p_note: null }, "edit agent");
}

export async function openRequest(item) {
  if (HUB_STUB) return { ok: true };
  // fn_request_open takes p_owner_id — the new owner (confirmed 2026-09-07). "Open" is
  // self-assign: the person opening the request takes ownership, so pass the caller's
  // auth id (the same identity fn_now uses for "Mine").
  const { data: sess } = await supabase.auth.getSession();
  const ownerId = sess?.session?.user?.id ?? null;
  return nowRpc("fn_request_open", { p_id: item.id, p_owner_id: ownerId }, "open request");
}

/**
 * The signed-in user's auth id, or null when there is no session. Used to offer
 * "Assign to me" only when that id is one of the assignable team members.
 * @returns {Promise<string|null>}
 */
export async function getMyUserId() {
  if (HUB_STUB) return null;
  const { data: sess } = await supabase.auth.getSession();
  return sess?.session?.user?.id ?? null;
}

// Thread replies are sent by the shared kit/Composer (its own composeSend), so the Now
// data layer no longer wraps fn_compose_send here.
